<template>
  <div class="min-h-screen bg-pitch-black text-total-white">
    <!-- Top Navbar -->
    <TopNavbar />

    <!-- Header -->
    <header class="flex justify-between items-center bg-dark-grey px-4 py-4 mb-6">
      <h1 class="text-xl font-semibold">Create Agent</h1>
      <div class="flex gap-2 text-sm">
        <img
          v-if="isCreating"
          src="@/assets/images/spinner.svg"
          alt="Loading Spinner"
          class="text-prime-golden w-8 h-8"
        />
      
      </div>
    </header>

    <!-- Create Agent Form -->
    <form class="space-y-3 px-4" :class="{ 'opacity-50': isCreating }">
      <!-- Agent Name Field -->
      <div class="flex flex-col">
        <label for="agent-name" class="text-sm font-medium w-fit">Agent Name *</label>
        <input
          id="agent-name"
          type="text"
          v-model="agentName"
          @input="errors.agentName = ''"
          placeholder=""
          class="mt-2 block w-full bg-dark-grey border border-1 border-total-white-100 text-total-white px-3 py-2 rounded-lg outline-none focus:ring-2 focus:ring-prime-golden"
        />
        <p v-if="errors.agentName" class="text-red-500 text-sm text-left mt-1">
          {{ errors.agentName }}
        </p>
      </div>

      <!-- Model Name Selection Dropdown -->
      <div class="flex flex-col" v-if="availableModels.length > 0">
        <label class="text-sm font-medium w-fit">Model Name *</label>
        <SimpleDropdown
          v-model="selectedModel"
          :options="availableModelsDropdownOptions"
          placeholder="Select model"
        />
        <p v-if="errors.model" class="text-red-500 text-sm mt-1">
          {{ errors.model }}
        </p>
      </div>

      <!-- Conditional Fields: Only shown if a model is selected -->
      <div v-if="selectedModel">
        <!-- If model type contains "gpt", show Top_p and Temperature fields -->
        <div v-if="isGptModel">
          <div class="flex flex-col">
            <label class="text-sm font-medium w-fit">Top_p *</label>
            <input
              type="text"
              v-model="topP"
              @input="errors.topP = ''"
              placeholder=""
              class="mt-2 block w-full bg-dark-grey border border-1 border-total-white-100 text-total-white px-3 py-2 rounded-lg outline-none focus:ring-2 focus:ring-prime-golden"
            />
            <p v-if="errors.topP" class="text-red-500 text-sm text-left mt-1">
              {{ errors.topP }}
            </p>
          </div>
          <div class="flex flex-col">
            <label class="text-sm font-medium w-fit">Temperature *</label>
            <input
              type="text"
              v-model="temperature"
              @input="errors.temperature = ''"
              placeholder=""
              class="mt-2 block w-full bg-dark-grey border border-1 border-total-white-100 text-total-white px-3 py-2 rounded-lg outline-none focus:ring-2 focus:ring-prime-golden"
            />
            <p v-if="errors.temperature" class="text-red-500 text-sm text-left mt-1">
              {{ errors.temperature }}
            </p>
          </div>
        </div>
        <!-- Otherwise, show Reasoning Effort dropdown -->
        <div v-else>
          <div class="flex flex-col">
            <label class="text-sm font-medium w-fit">Reasoning Effort *</label>
            <SimpleDropdown
              v-model="reasoningEffort"
              :options="reasoningEffortOptions"
              placeholder="Select reasoning effort"
            />
          </div>
        </div>
      </div>

      <!-- Is Default Checkbox -->
      <div class="flex items-center gap-2 mt-4">
        <input type="checkbox" id="is_default" v-model="isDefault" class="hidden" />
        <div
          @click="isDefault = !isDefault"
          :class="[
            'w-5 h-5 rounded flex items-center justify-center border cursor-pointer',
            isDefault ? 'bg-dark-grey border-gray-700' : 'bg-gray-600 border-gray-700',
          ]"
        >
          <svg
            v-if="isDefault"
            xmlns="http://www.w3.org/2000/svg"
            class="h-4 w-4 text-prime-golden"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="3"
          >
            <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <label for="is_default" class="ml-1 text-sm font-medium">is default</label>
      </div>
      <div class="flex items-center text-sm gap-2 mt-6 justify-end">
      <button
          @click="submitForm"
          class="bg-prime-golden text-pitch-black px-3 py-1 rounded-lg font-semibold hover:opacity-90"
          :disabled="isCreating"
        >
          {{ isCreating ? "Creating..." : "Create" }}
        </button>
        <button
          @click="goBack"
          class="bg-gray-500 text-total-white px-3 py-1 rounded-lg font-semibold hover:bg-gray-400"
          >
            Cancel
          </button>
      </div>
    </form>
  </div>
</template>

<script setup>
import TopNavbar from "@/components/TopNavbar.vue";
import { useRouter } from "vue-router";
import { ref, computed, onMounted } from "vue";
import { useAgentStore } from "@/stores/agent";
import SimpleDropdown from "@/components/SimpleDropdown.vue"; // New simple dropdown component

const router = useRouter();
const agentStore = useAgentStore();

const agentName = ref("");
const topP = ref("");
const temperature = ref("");
const isDefault = ref(false);
const isCreating = ref(false);
const errors = ref({});

// Model selection fields
const availableModels = ref([]);
const selectedModel = ref(null);
const reasoningEffort = ref("medium");

// Prepare dropdown options for available models
const availableModelsDropdownOptions = computed(() =>
  availableModels.value.map((model) => ({
    label: model.model_name,
    value: model,
  }))
);

// Options for reasoning effort dropdown
const reasoningEffortOptions = [
  { label: "low", value: "low" },
  { label: "medium", value: "medium" },
  { label: "high", value: "high" },
];

// Computed: is the selected model a GPT-based model?
const isGptModel = computed(() => {
  return (
    selectedModel.value &&
    selectedModel.value.model_type &&
    selectedModel.value.model_type.toLowerCase().includes("gpt")
  );
});

// Fetch available models on mount
onMounted(async () => {
  try {
    const response = await agentStore.fetchAvailableModels();
    availableModels.value = response.data;
  } catch (error) {
    console.error("Error fetching available models:", error);
  }
});

const submitForm = async () => {
  errors.value = {};
  if (!agentName.value) {
    errors.value.agentName = "Agent Name is required.";
  }
  if (!selectedModel.value) {
    errors.value.model = "Please select a model.";
  }
  if (selectedModel.value) {
    if (isGptModel.value) {
      if (!topP.value || isNaN(topP.value)) {
        errors.value.topP = "Top_p is required and must be a number.";
      }
      if (topP.value < 0 || topP.value > 1) {
        errors.value.topP = "Top_p must be between 0 and 1.";
      }
      if (!temperature.value || isNaN(temperature.value)) {
        errors.value.temperature = "Temperature is required and must be a number.";
      }
      if (temperature.value < 0 || temperature.value > 2) {
        errors.value.temperature = "Temperature must be between 0 and 2.";
      }
    }
  }
  if (Object.keys(errors.value).length > 0) return;

  try {
    isCreating.value = true;
    let agentData = {
      agent_name: agentName.value,
      model_name: selectedModel.value.model_name,
      provider_name: "OpenAI",
      is_default: isDefault.value,
    };
    if (isGptModel.value) {
      agentData.top_p = topP.value;
      agentData.temperature = temperature.value;
    } else {
      agentData.reasoning_effort = reasoningEffort.value;
    }
    await agentStore.createAgent(agentData);
    router.push({ name: "AgentsPage" });
  } catch (error) {
    console.error("Error submitting form:", error);
  } finally {
    isCreating.value = false;
  }
};

const goBack = () => {
  router.push({ name: "AgentsPage" });
};

const handleModelChange = () => {
  topP.value = "";
  temperature.value = "";
  reasoningEffort.value = "medium";
};
</script>
